<template>
    <Dimmer :hide-spinner="false" :model-value="progress">
        <template #content>
            <v-row>
                <v-col class="px-0">
                    <v-card v-if="!isMessageSent" flat>
                        <Form ref="elFormObj" as="" v-slot="{ errors: formErrors, meta: formMeta, resetForm }">
                            <form @submit="onSubmit($event)" @reset="onReset($event)" novalidate>
                                <!--
                                    Honeypot: hidden from real users (off-screen, not tabbable,
                                    hidden from screen readers, autofill suppressed). Bots that
                                    fill every field will populate it; the backend rejects any
                                    submission where `company` is non-empty.
                                -->
                                <div class="hp-field" aria-hidden="true">
                                    <input
                                        v-model="honeypot"
                                        type="text"
                                        name="company"
                                        tabindex="-1"
                                        autocomplete="off"
                                        data-lpignore="true"
                                        data-1p-ignore="true"
                                    />
                                </div>

                                <v-card-text>

                                    <!-- Name -->
                                    <v-row>
                                        <v-col>
                                            <Field
                                                name="fromName"
                                                label="Name"
                                                v-slot="{ field, errors }"
                                                v-model="form.fromName"
                                                rules="required"
                                            >
                                                <v-text-field
                                                    v-bind="field"
                                                    :model-value="field.value"
                                                    :error-messages="errors"
                                                    name="fromName"
                                                    label="Name"
                                                    autocomplete="name"
                                                    variant="outlined"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                    </v-row>

                                    <!-- Email -->
                                    <v-row>
                                        <v-col>
                                            <Field
                                                name="email"
                                                label="Email"
                                                v-slot="{ field, errors }"
                                                v-model="form.fromEmail"
                                                :rules="{ email: true, required: true }"
                                            >
                                                <v-text-field
                                                    v-bind="field"
                                                    :model-value="field.value"
                                                    name="email"
                                                    label="Email"
                                                    type="email"
                                                    autocorrect="off"
                                                    autocapitalize="none"
                                                    autocomplete="email"
                                                    variant="outlined"
                                                    :error-messages="errors"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                    </v-row>

                                    <!-- Phone -->
                                    <v-row>
                                        <v-col>
                                            <Field
                                                name="phone"
                                                label="Phone Number"
                                                v-slot="{ field, errors }"
                                                v-model="form.phone"
                                            >
                                                <v-text-field
                                                    v-bind="field"
                                                    :model-value="field.value"
                                                    :error-messages="errors"
                                                    name="phone"
                                                    label="Phone Number"
                                                    autocomplete="tel"
                                                    variant="outlined"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                    </v-row>

                                    <!-- Subject -->
                                    <v-row>
                                        <v-col>
                                            <Field
                                                name="subject"
                                                label="Subject"
                                                v-slot="{ field, errors }"
                                                v-model="form.subject"
                                                rules="required"
                                            >
                                                <v-text-field
                                                    v-bind="field"
                                                    :model-value="field.value"
                                                    :error-messages="errors"
                                                    name="subject"
                                                    label="Subject"
                                                    :readonly="!!selectedSubject"
                                                    variant="outlined"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                    </v-row>

                                    <!-- Recipient -->
                                    <v-row>
                                        <v-col>
                                            <Field
                                                name="recipient"
                                                label="Department"
                                                v-slot="{ field, errors }"
                                                v-model="form.recipient"
                                                rules="required"
                                            >
                                                <v-select
                                                    v-bind="field"
                                                    :model-value="field.value"
                                                    item-title="text"
                                                    :items="Array.isArray(recipient) ? recipient : []"
                                                    :error-messages="errors"
                                                    name="recipient"
                                                    label="Department"
                                                    data-vv-name="recipient"
                                                    :readonly="!!selectedRecipient"
                                                    :loading="loadingRecipients"
                                                    variant="outlined"
                                                ></v-select>
                                            </Field>
                                        </v-col>
                                    </v-row>

                                    <!-- Message -->
                                    <v-row>
                                        <v-col>
                                            <Field
                                                name="message"
                                                label="Message"
                                                v-slot="{ field, errors }"
                                                v-model="form.message"
                                                rules="required|max:5000"
                                            >
                                                <v-textarea
                                                    v-bind="field"
                                                    :model-value="field.value"
                                                    :error-messages="errors"
                                                    name="message"
                                                    label="Message"
                                                    counter="5000"
                                                    variant="outlined"
                                                ></v-textarea>
                                            </Field>
                                        </v-col>
                                    </v-row>
                                </v-card-text>

                                <v-card-actions class="justify-end">
                                    <v-btn
                                        type="reset"
                                        size="large"
                                        color="accent"
                                        :disabled="!formMeta.dirty"
                                    >
                                        Clear
                                    </v-btn>
                                    <v-btn
                                        variant="flat"
                                        class="mr-4"
                                        color="primary"
                                        type="submit"
                                        :disabled="!(formMeta.dirty || formMeta.touched) || !lo_isEmpty(formErrors)"
                                        size="large"
                                    >
                                        Submit
                                    </v-btn>
                                </v-card-actions>

                                <v-card-text
                                    v-if="recaptchaEnabled"
                                    class="text-caption text-medium-emphasis pt-0"
                                >
                                    This site is protected by reCAPTCHA and the Google
                                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                    and
                                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                                    apply.
                                </v-card-text>
                            </form>
                        </Form>
                    </v-card>
                    <div v-else>
                        <h2>Message Sent!</h2>
                        <p class="mt-4">
                            Back to
                            <NuxtLink to="/">Home Page</NuxtLink>
                        </p>
                    </div>
                </v-col>
            </v-row>
        </template>
    </Dimmer>
</template>

<script lang="ts" setup>
    import type { Field, Form } from 'vee-validate';
    import type { ContactUsRecipient, ContactUsMessageRequest } from '~/types';
    import { ContactFormModel } from '~/models';
    import { merge as lo_mergeCompat } from 'es-toolkit/compat';
    import { isError as lo_isError } from 'es-toolkit';
    import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
    import { useContact, useErrorMessage, useRecaptcha } from '~/composables';
    import { useGlobalAlert } from 'x1-base/composables';
    import Dimmer from 'x1-base/components/Dimmer/Dimmer.vue';
    import { initVeeRules } from '~/helpers/forms';

    initVeeRules([ 'required', 'email', 'max' ]);

    const props = defineProps<{
        fromName?: Nilish<string>;
        fromEmail?: Nilish<string>;
        selectedSubject?: Nilish<string>;
        selectedRecipient?: Nilish<string>;
    }>();

    const useContactObj = useContact();
    const { execute: executeRecaptcha, isEnabled: recaptchaEnabled } = useRecaptcha('contact_us');
    const elFormObj = shallowRef<typeof Form>();
    const createGlobalAlert = useGlobalAlert();
    const isMessageSent = ref(false);
    const progress = ref<boolean>(false);
    // Honeypot — must stay empty for real users; non-empty = bot (rejected server-side).
    const honeypot = ref('');

    const { data: recipient, pending: loadingRecipients } = useLazyAsyncData<ContactUsRecipient | Error>(
        'keyContactRecipients',
        () => useContactObj.getRecipients()
    );

    const model = ContactFormModel.toPlainObject<ContactFormModel>({
        fromName: props.fromName ?? '',
        fromEmail: props.fromEmail ?? '',
        recipient: props.selectedRecipient ?? 'info',
        subject: props.selectedSubject ?? ''
    });
    const form = reactive<ContactFormModel>(model);

    async function onSubmit(e: Event) {
        e.preventDefault();

        const validateObj = await elFormObj.value?.validate();

        if (!validateObj.valid) {
            return;
        }

        progress.value = true;

        const payload = ContactFormModel.toPlainObject<ContactUsMessageRequest>(form);
        payload.company = honeypot.value;
        payload.recaptchaToken = await executeRecaptcha('contact_us');

        const resp = await useContactObj.sendMessage(payload);
        progress.value = false;

        if (!lo_isError(resp)) {
            isMessageSent.value = true;
        } else {
            createGlobalAlert({
                text: useErrorMessage('globalAlert.contactForm.cannotSubmit'),
                show: true,
                type: 'error'
            });
        }
    }

    function onReset(e: Event) {
        e.preventDefault();
        lo_mergeCompat(
            form,
            ContactFormModel.toPlainObject<ContactFormModel>({
                fromName: props.fromName ?? '',
                fromEmail: props.fromEmail ?? '',
                recipient: props.selectedRecipient ?? 'info',
                subject: props.selectedSubject ?? 'Request for Information'
            })
        );

        honeypot.value = '';
        elFormObj.value?.resetForm();
    }
</script>

<style lang="scss" scoped>
    // Honeypot wrapper — keep it in the DOM (so bots find it) but invisible and
    // inert for real users. Off-screen rather than display:none so naive bots
    // that skip hidden inputs still fill it.
    .hp-field {
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
    }
</style>
